import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../helpers/constants";
import { TransferParticipantResult } from "../../../pages/createBulkTransfer/modelsAndSchemas/Models";
import apiInstance from "../../instances/apiInstance";
import {
  CompatibleTemplateStatusListByProgramsIdParams,
  GetParticipantTransferDestinationProgramParams,
  SaveBulkParticipantTransferParams,
  SaveBulkParticipantTransferResponse,
} from "./models";

export const useGetBulkTransferCompatibleTemplateList = () => {
  return useMutation({
    mutationFn: (params: CompatibleTemplateStatusListByProgramsIdParams) => {
      return apiInstance.post(
        API_ENDPOINTS.GET_BULK_PARTICIPANT_COMPATIBLE_PROGRAM_TEMPLATES,
        params,
      );
    },
    retry: false,
  });
};

export const useSaveBulkParticipantTransfer = () => {
  return useMutation({
    mutationFn: async (
      params: SaveBulkParticipantTransferParams,
    ): Promise<SaveBulkParticipantTransferResponse> => {
      const res = await apiInstance.post(
        API_ENDPOINTS.SAVE_BULK_PARTICIPANT_TRANSFER,
        params,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useGetBulkParticipantTransferDestinationProgramDetail = () => {
  return useMutation({
    mutationFn: (params: GetParticipantTransferDestinationProgramParams) => {
      return apiInstance.post(
        API_ENDPOINTS.GET_BULK_PARTICIPANT_TRANSFER_DESTINATION_PROGRAM_DETAIL,
        params,
      );
    },
    retry: false,
  });
};

export const useGetNextLanguages = () => {
  const [params, setParams] = useState<{
    countryCode?: string;
  }>({});

  const query = useQuery({
    queryKey: ["useGetNextLanguages", params.countryCode],
    queryFn: async () => {
      if (!params.countryCode) return [];

      let url = API_ENDPOINTS.GET_NEXT_LANGUAGES;
      const queryParams = new URLSearchParams();
      if (params.countryCode) {
        queryParams.append("countryCode", params.countryCode);
      }
      if (queryParams.toString()) {
        url = `${url}?${queryParams.toString()}`;
      }
      const res = await apiInstance.get(url);
      return res.data || [];
    },
    enabled: !!params.countryCode,

    retry: false,
  });

  const refetchWithParams = (countryCode?: string) => {
    setParams({ countryCode });
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useGetBulkTransferResult = () => {
  const [params, setParams] = useState<string>("");

  const query = useQuery({
    queryKey: ["useGetBulkTransferResult", params],
    queryFn: async (): Promise<TransferParticipantResult> => {
      if (!params) return {} as TransferParticipantResult;
      const res = await apiInstance.get(
        API_ENDPOINTS.getBulkTransferResult(params),
      );
      return res.data;
    },
    enabled: !!params,
    retry: false,
  });

  const refetchWithParams = (newParams: string) => {
    setParams(newParams);

    query.refetch();
  };

  return { ...query, refetchWithParams };
};
